import { flow, pipe, ReadonlyArray, Runtime } from "fp";

import { Asset } from "~/Asset";
import { Company } from "~/Company";
import * as Data from "~/Data";
import * as Driver from "~/Driver";
import { Order } from "~/Order";
import { Remote } from "~/Remote";
import { Search } from "~/Search";
import { Time } from "~/Time";

import * as Request from "./Request";

export { Request };

export type Pairing = Time.Interval &
  Asset & { readonly driver: Data.Reference.Reference };

export const isActive = (pairing: Pairing): boolean =>
  !Time.Interval.isStopped(pairing);

export const { equals, compare } = Order.fromCompare<Pairing>((a, b) =>
  pipe([isActive(a), isActive(b)], ([isActiveA, isActiveB]) =>
    !isActiveA && isActiveB
      ? Order.lessThan()
      : !isActiveB && isActiveA
      ? Order.greaterThan()
      : Time.Interval.compare(a, b)
  )
);

export const descendingOrder = Order.fromCompare(flow(compare, Order.reverse));

export const toSearch: Search.From<Pairing> = (pairing) =>
  Search.join(Time.Interval.toSearch(pairing), Asset.toSearch(pairing));

export const useDriverPairings = ({
  driver,
  dateTime,
}: Partial<Time.DateTime> & {
  readonly driver: Data.Reference.Reference;
}) => {
  return Remote.Query.use<Record<string, readonly Driver.Pairing.Pairing[]>>(
    Request.Query.Driver.key({ driver, dateTime }),
    Request.Query.Driver.query({
      driver,
      dateTime,
    }),
    {
      keepPreviousData: true,
      staleTime: Time.Duration.minutes(5).duration,
    }
  );
};

export const useAllSorted = ({
  driver,
}: {
  readonly driver: Data.Reference.Reference;
}): readonly Driver.Pairing.Pairing[] => {
  if (driver) {
    return pipe(
      Remote.toUndefined(useDriverPairings({ driver })) ?? {},
      (pairings) => pairings[driver.id],
      ReadonlyArray.sort(descendingOrder)
    );
  }
  return [];
};

export const useFromDriverAndDateTime = function ({
  driver,
  dateTime,
}: Time.DateTime & {
  readonly driver?: Data.Reference.Reference;
}): readonly Pairing[] {
  if (driver) {
    return Remote.when(Driver.Pairing.useDriverPairings({ driver, dateTime }), {
      loading: () => [],
      error: () => [],
      success: flow(
        (pairings) => (driver ? pairings[driver.id] ?? [] : []),
        pairingsOverlappingInterval(Time.DateTime.toDayInterval({ dateTime }))
      ),
    });
  }
  return [];
};

const pairingsOverlappingInterval = ({
  interval,
}: Time.Interval.Stopped): ((
  pairings: readonly Pairing[]
) => readonly Pairing[]) =>
  ReadonlyArray.filter((pairing) =>
    Time.Interval.Stopped.collides({ interval })(
      Time.Interval.when(pairing, {
        stopped: (stopped) => stopped,
        started: ({ interval: { start } }) => ({
          interval: { start, stop: interval.stop },
        }),
      })
    )
  );

export const useFilteredPairingsQuery = (
  reportingLocationIds?: readonly number[]
): Data.Remote<Record<string, readonly Driver.Pairing.Pairing[]>> => {
  const company = Company.use();
  const workReportingLocationIds = reportingLocationIds
    ? [...reportingLocationIds]
    : [];
  return Remote.Query.use<Record<string, readonly Driver.Pairing.Pairing[]>>(
    Request.Query.Company.key({
      reportingLocationIds: workReportingLocationIds,
    }),
    Request.Query.Company.query({
      ...company,
      reportingLocationIds: workReportingLocationIds,
    }),
    {
      enabled: !!reportingLocationIds,
      keepPreviousData: true,
      staleTime: Time.Duration.minutes(15).duration,
    }
  );
};

export const useFilteredPairingsRemote = () => {
  const locationIds = Driver.Table.Filter.Location.useSelectedWorkReportingLocations();
  return useFilteredPairingsQuery(locationIds);
};

export const useFilteredPairings = ():
  | Record<string, readonly Driver.Pairing.Pairing[]>
  | undefined => {
  const [loading, pairings] = Remote.when(useFilteredPairingsRemote(), {
    loading: () => [true, {}],
    error: () => [false, {}],
    success: (pairings) => {
      return [false, pairings];
    },
  });

  return loading ? {} : pairings;
};

export const useUnfilteredPairingsQuery = ({
  interval,
}: Time.Interval.Stopped): Data.Remote<
  Record<string, readonly Driver.Pairing.Pairing[]>
> => {
  const company = Company.use();
  return Remote.Query.use<Record<string, readonly Driver.Pairing.Pairing[]>>(
    Request.Query.Company.key({
      reportingLocationIds: [],
      dateRange: { interval },
    }),
    Request.Query.Company.query({
      ...company,
      reportingLocationIds: [],
      dateRange: { interval },
    }),
    {
      keepPreviousData: true,
      staleTime: Time.Duration.minutes(15).duration,
    }
  );
};

export type UseFilteredPairingsResult = {
  readonly isLoading: boolean;
  readonly pairings?: Record<string, readonly Driver.Pairing.Pairing[]>;
};

export const useUnfilteredPairings = ({
  interval,
}: Time.Interval.Stopped): UseFilteredPairingsResult =>
  Remote.when<
    Record<string, readonly Driver.Pairing.Pairing[]>,
    UseFilteredPairingsResult
  >(useUnfilteredPairingsQuery({ interval }), {
    loading: () => ({ isLoading: true, pairings: undefined }),
    error: () => ({ isLoading: false, pairings: undefined }),
    success: (pairings) => ({ isLoading: false, pairings }),
  });

export namespace Response {
  export const runtime = () =>
    Runtime.strict({
      driver_id: Runtime.number(),
      start_date: Time.DateTime.Response.runtime(),
      end_date: Runtime.nullable(Time.DateTime.Response.runtime()),
      asset: Asset.Response.runtime(),
    });

  export const runtimeArray = () => Runtime.array(runtime());

  export const decode = (
    response: Runtime.ToType<typeof runtime>
  ): Pairing => ({
    ...Asset.Response.decode(response.asset),
    driver: { id: `${response.driver_id}` },
    interval: {
      start: response.start_date,
      stop: response.end_date ?? undefined,
    },
  });
}
